import { ref, unref, onUnmounted, Ref, isRef, watch, toRefs } from 'vue'
import { useAccessToken } from './useAccessToken'
import axios, { AxiosInstance, CancelTokenSource } from 'axios'
import isEqual from 'lodash/isEqual'

export type HttpMethod = 'GET' | 'POST'

export type FetchPostResult<RequestBody, Data> = {
  data: Ref<Data | undefined>
  error: Ref<Error | undefined>
  isLoading: Ref<boolean>
  query: Ref<RequestBody | undefined>
  cancel: () => void
  fetch: (body: RequestBody | null, totalRetries?: number) => Promise<void>
}

export type FetchGetResult<Data> = {
  data: Ref<Data | undefined>
  error: Ref<Error | undefined>
  isLoading: Ref<boolean>
  cancel: () => void
  fetch: (totalRetries?: number) => Promise<void>
}

export type FetchResult<Type, RequestBody, Data> = Type extends 'GET'
  ? FetchGetResult<Data>
  : Type extends 'POST'
  ? FetchPostResult<RequestBody, Data>
  : never

const API_BASE_URL = process.env.VUE_APP_API_GW_BASE_URL
const TIMEOUT = 30000
const DEFAULT_RETRIES = 5
const INITIAL_RETRY_DELAY = 2000

function createAxiosClient(): AxiosInstance {
  return axios.create({
    baseURL: API_BASE_URL,
    timeout: TIMEOUT,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

async function doFetch<Data, RequestBody>(
  client: AxiosInstance,
  cancelTokenSource: CancelTokenSource,
  url: string | undefined,
  method: HttpMethod | undefined,
  body: RequestBody | null,
  totalRetries: number,
  retry: number = 0,
  retryDelay: number = INITIAL_RETRY_DELAY
): Promise<Data | undefined> {
  if (url === undefined || method === undefined) return
  if (body === null && method === 'POST') return

  try {
    const response = await client.request<Data>({
      url,
      method,
      data: body,
      cancelToken: cancelTokenSource.token
    })

    return response.data
  } catch (err) {
    if (axios.isCancel(err)) {
      console.debug(`Fetch ${url} was cancelled.`)
      throw err
    }

    if (retry >= totalRetries) {
      console.debug(`Giving up on fetch after ${totalRetries} retries.`)
      throw err
    }

    console.debug(`Retrying fetch ...`)
    await new Promise((resolve) => setTimeout(resolve, retryDelay))
    return doFetch(
      client,
      cancelTokenSource,
      url,
      method,
      body,
      totalRetries,
      retry + 1,
      retryDelay * 2 // exponential backoff
    )
  }
}

export function useFetch<Data>(
  url: string | Ref<string | undefined>,
  method: 'GET',
  fetchOnlyIf?: () => boolean,
  dataTranform?: (data: Data | undefined) => Partial<Data> | undefined
): FetchGetResult<Data>
export function useFetch<RequestBody, Data>(
  url: string | Ref<string | undefined>,
  method: 'POST',
  fetchOnlyIf?: (query: RequestBody | null) => boolean,
  dataTranform?: (data: Data | undefined) => Partial<Data> | undefined
): FetchPostResult<RequestBody, Data>
export function useFetch<RequestBody, Data>(
  url: string | Ref<string | undefined>,
  method: Ref<HttpMethod | undefined>,
  fetchOnlyIf?: (query: RequestBody | null) => boolean,
  dataTranform?: (data: Data | undefined) => Partial<Data> | undefined
): FetchPostResult<RequestBody, Data>
export function useFetch<Data, RequestBody>(
  url: string | Ref<string | undefined>,
  method: HttpMethod | Ref<HttpMethod | undefined>,
  fetchOnlyIf: (query: RequestBody | null) => boolean = () => true,
  dataTranform: (data: Data | undefined) => Partial<Data> | undefined = (data) => data
) {
  const data = ref<Data | undefined>(undefined)
  const error = ref<Error | undefined>(undefined)
  const query = ref<RequestBody | undefined>(undefined)
  const isLoading = ref<boolean>(false)
  const access = useAccessToken()
  const client = createAxiosClient()
  const httpMethod = isRef(method) ? method : ref(method)

  let cancelTokenSource = axios.CancelToken.source()
  const { bearer } = toRefs(access)

  watch(
    bearer,
    () => {
      if (bearer.value) {
        client.defaults.headers.Authorization = bearer.value
      }
    },
    { immediate: true }
  )

  async function fetchWrapper(body: RequestBody | null, totalRetries: number = DEFAULT_RETRIES) {
    if (!fetchOnlyIf(body)) return
    isLoading.value = true

    try {
      const res = await doFetch<any, RequestBody>(
        client,
        cancelTokenSource,
        unref(url),
        httpMethod.value,
        body,
        totalRetries
      )
      // @ts-ignore
      data.value = dataTranform(res)
      error.value = undefined
    } catch (err: any) {
      error.value = err
      console.log('Error:', err.message)
      data.value = undefined
    } finally {
      isLoading.value = false
    }
  }

  const cancel = () => {
    cancelTokenSource.cancel(`Aborting fetch ${url}...`)
    cancelTokenSource = axios.CancelToken.source()
    isLoading.value = false
  }

  onUnmounted(cancel)

  return {
    data,
    error,
    isLoading,
    query,
    cancel,
    fetch: (body: RequestBody | null, totalRetries: number = DEFAULT_RETRIES) =>
      fetchWrapper(body, totalRetries)
  } as FetchResult<'POST' | 'GET', RequestBody, Data>
}

export function useFetchGet<Data>(
  url: string | Ref<string | undefined>,
  dataTranform?: (data: Data | undefined) => Partial<Data> | undefined
) {
  const { data, error, isLoading, cancel, fetch } = useFetch<Data>(
    url,
    'GET',
    () => true,
    dataTranform
  )
  if (isRef(url))
    watch(
      url,
      (newUrl) => {
        if (newUrl !== undefined) {
          cancel()
          fetch()
        }
      },
      { immediate: true }
    )
  else fetch()
  return { data, error, isLoading, cancel, fetch }
}

export function useFetchPost<Body, Data>(
  url: string | Ref<string | undefined>,
  body: (Body | null) | Ref<Body | null>,
  fetchOnlyIf: (body: Body | null) => boolean = () => true,
  dataTranform?: (data: Data | undefined) => Partial<Data> | undefined
) {
  const { data, error, isLoading, cancel, fetch } = useFetch<Body, Data>(
    url,
    'POST',
    fetchOnlyIf,
    dataTranform
  )
  const refs = [url, body].filter((x) => isRef(x))
  if (refs.length > 0) {
    watch(
      refs,
      () => {
        const b = unref(body)
        fetch(b ? b : null)
      },
      { immediate: true }
    )
  } else {
    fetch(unref(body))
  }
  return { data, error, isLoading, cancel, fetch }
}

export function useFetchGeneral<Body, Data>(
  url: Ref<string | undefined>,
  method: Ref<HttpMethod | undefined>,
  body: Ref<Body | null>,
  fetchOnlyIf: (body: Body | null) => boolean = () => true
) {
  const { data, error, isLoading, cancel, fetch } = useFetch<Body, Data>(url, method, fetchOnlyIf)
  const oldUrl = ref<string | undefined>(undefined)

  function newFetch() {
    if (method.value === 'GET') {
      cancel()
      fetch(null)
    } else if (method.value === 'POST') {
      fetch(body.value ?? null)
    }
  }
  watch(
    [url, body, method],
    ([newUrl, newBody, newMethod], [, oldBody]) => {
      if (newMethod === 'GET' && newUrl !== oldUrl.value) {
        cancel()
        fetch(null)
        oldUrl.value = newUrl
      } else if (newMethod === 'POST' && !isEqual(newBody, oldBody)) {
        fetch(newBody ?? null)
        oldUrl.value = newUrl
      }
    },
    { immediate: true }
  )
  return { data, error, isLoading, cancel, fetch: newFetch }
}
